import { DefaultTeamLogo, useIntl } from '@capsicum/common';
import { AmountSelection, ButtonInSection, type ButtonProps, ButtonSection, useRouter } from '@capsicum/common-web';
import {
	ncwSendAssetAtom,
	useNCWSendTokenAmount,
	useNCWSendTokenAmountTexts
} from '@capsicum/features/nc-wallet/shared';
import { convertNcwBalanceCurrencies, useNCWBalance } from '@capsicum/socios-core';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { NCWBalanceInfo } from '../../../ui';
import * as S from '../amountComponents.styled';

import { SendTokensAlert } from './SendTokensAlert';

/**
 * Part of the NCW Send Flow. Users can enter the Fan Token amount to be sent.
 */
export const NCWSendTokenAmount = () => {
	const { $t } = useIntl();
	const sendAsset = useAtomValue(ncwSendAssetAtom);
	const { currencies, isPending: isBalancePending, ncwBalance, tokenBalances } = useNCWBalance();
	// TODO: to remove; awaiting BE to fix currencies data type.
	const currenciesArray = convertNcwBalanceCurrencies(ncwBalance?.currencies);
	const tokenBalanceRaw = currenciesArray.find(c => c.currency.shortCode === sendAsset?.shortCode);
	const tokenBalance = tokenBalances.find(tb => tb.shortCode === sendAsset?.shortCode);
	const { conversionAltPrice, convertedDefaultPrice, error, inputValue, setError, setInputValue } =
		useNCWSendTokenAmount(tokenBalanceRaw, currencies, !!sendAsset?.decimalDetails);
	const { intlText } = useNCWSendTokenAmountTexts();
	const minAmount = sendAsset?.decimalDetails ? 0 : 1;
	const isAmountInvalid =
		inputValue === undefined || inputValue < minAmount || inputValue > (tokenBalance?.usableAmount || 0);

	// Set the AmountSelection error if the entering amount is invalid
	useEffect(() => {
		if (isAmountInvalid) {
			setError({
				message: $t('ncWallet.send.enterAmount.invalidAmount', {
					maxValue: Number(tokenBalance?.usableAmount),
					minValue: minAmount,
					shortCode: tokenBalance?.shortCode
				}),
				type: 'error'
			});
		} else {
			setError(undefined);
		}
	}, [isAmountInvalid]);

	// TODO: supply the estimated network fee when BE API is available.
	const insufficientChilizAmount = 1.99 > Number(tokenBalanceRaw?.totalAmountChz);

	const { push } = useRouter();
	const buttonProps = {
		disabled: isBalancePending || isAmountInvalid || !inputValue,
		isLoading: isBalancePending,
		onClick: () => {
			push('recipients');
		},
		testID: 'ncw-send-tokenAmount-continue-btn',
		text: intlText.buttonText
	} satisfies ButtonProps;

	return (
		<S.Wrapper>
			<S.Container>
				<S.Title variant="title5">{intlText.pageTitle}</S.Title>
				<NCWBalanceInfo
					altAmount={tokenBalance?.tokenCardData.usableFormattedAltBalance}
					balanceAmount={tokenBalance?.tokenCardData.usableTokenBalance ?? ''}
					hasDecimals={!!sendAsset?.decimalDetails}
					isLoading={isBalancePending}
				/>
				<AmountSelection
					altCurrencyAmount={!sendAsset?.decimalDetails ? conversionAltPrice : undefined}
					conversionPrice={!sendAsset?.decimalDetails ? convertedDefaultPrice : undefined}
					currencyLogo={tokenBalance?.tokenCardData.tokenImgUrl || DefaultTeamLogo}
					currencyShortName={sendAsset?.shortCode}
					decimalDetail={sendAsset?.decimalDetails ? 2 : 0}
					defaultCurrencyAmount={!sendAsset?.decimalDetails ? `1 ${sendAsset?.shortCode}` : undefined}
					error={error}
					isConversionRatesLoading={isBalancePending}
					max={tokenBalance?.usableAmount}
					min={minAmount}
					testID="ncw-select-tokenAmount-numberField"
					value={inputValue?.toString()}
					onChange={amount => setInputValue(amount)}
				/>
				{/*
					We will show network fees here at some point in the future
					<NetworkFeeCard />
				*/}
				<SendTokensAlert alertType="message" />
				<ButtonSection>
					<ButtonInSection {...buttonProps} />
				</ButtonSection>
			</S.Container>
		</S.Wrapper>
	);
};
